/**
 * ReplaceMenu — Tabbed popover panel for replacing an element with a compatible type.
 *
 * Preserves connections and compatible properties when swapping types.
 * Compatible type groups:
 *   - Tasks ↔ Tasks (User, Service, Script, Manual, Send, Receive, BusinessRule, CallActivity)
 *   - Gateways ↔ Gateways (Exclusive, Parallel, Inclusive, Complex, EventBased)
 *   - Start Events (different triggers)
 *   - End Events (different triggers)
 *   - Intermediate Catch Events ↔ Intermediate Throw Events
 */

// ── SVG icons per type (colored, matching epx-flow canvas icons) ─────────────

const TYPE_ICONS = {
  // Tasks
  'epx:Task': `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#f5f5f5" stroke="#595959" stroke-width="1.5"/>
  </svg>`,

  'epx:UserTask': `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#e6f7ff" stroke="#1890ff" stroke-width="1.5"/>
    <circle cx="7" cy="6" r="2" fill="none" stroke="#1890ff" stroke-width="1"/>
    <path d="M 4 13 Q 7 10 10 13" fill="none" stroke="#1890ff" stroke-width="1"/>
  </svg>`,

  'epx:ServiceTask': `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#f9f0ff" stroke="#722ed1" stroke-width="1.5"/>
    <circle cx="8" cy="8" r="3" fill="none" stroke="#722ed1" stroke-width="1"/>
    <circle cx="8" cy="8" r="1" fill="#722ed1"/>
  </svg>`,

  'epx:SendTask': `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#f0f5ff" stroke="#1d39c4" stroke-width="1.5"/>
    <path d="M 5 4 L 10 8 L 15 4 L 15 11 L 5 11 Z" fill="#1d39c4" stroke="#1d39c4" stroke-width="0.8"/>
    <path d="M 5 4 L 10 8 L 15 4" fill="none" stroke="#fff" stroke-width="0.8"/>
  </svg>`,

  'epx:ReceiveTask': `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#e6f7ff" stroke="#40a9ff" stroke-width="1.5"/>
    <rect x="5" y="4" width="10" height="7" fill="none" stroke="#40a9ff" stroke-width="0.9"/>
    <path d="M 5 4 L 10 8 L 15 4" fill="none" stroke="#40a9ff" stroke-width="0.9"/>
  </svg>`,

  'epx:ScriptTask': `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#fff7e6" stroke="#fa8c16" stroke-width="1.5"/>
    <line x1="5" y1="5" x2="13" y2="5" stroke="#fa8c16" stroke-width="0.9"/>
    <line x1="5" y1="8" x2="13" y2="8" stroke="#fa8c16" stroke-width="0.9"/>
    <line x1="5" y1="11" x2="10" y2="11" stroke="#fa8c16" stroke-width="0.9"/>
  </svg>`,

  'epx:ManualTask': `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#e6fffb" stroke="#13c2c2" stroke-width="1.5"/>
    <path d="M 6 12 L 6 7 C 6 5 8 4 9 6 L 10 5 C 11 4 12 5 12 6 L 13 6 L 13 12 Z" fill="none" stroke="#13c2c2" stroke-width="0.9"/>
  </svg>`,

  'epx:BusinessRuleTask': `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#fffbe6" stroke="#d48806" stroke-width="1.5"/>
    <rect x="5" y="3" width="10" height="9" fill="none" stroke="#d48806" stroke-width="0.8"/>
    <rect x="5" y="3" width="10" height="3" fill="#d48806" stroke="#d48806" stroke-width="0.8"/>
    <line x1="5" y1="9" x2="15" y2="9" stroke="#d48806" stroke-width="0.6"/>
    <line x1="9" y1="6" x2="9" y2="12" stroke="#d48806" stroke-width="0.6"/>
  </svg>`,

  'epx:CallActivity': `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#f6ffed" stroke="#389e0d" stroke-width="2.5"/>
    <path d="M 6 5 L 11 5 L 11 3 L 14 7.5 L 11 12 L 11 10 L 6 10 Z" fill="none" stroke="#389e0d" stroke-width="0.9"/>
  </svg>`,

  'epx:SubProcess': `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#e6f7ff" stroke="#096dd9" stroke-width="2"/>
    <rect x="6" y="8" width="8" height="5" rx="1" fill="none" stroke="#096dd9" stroke-width="0.8"/>
    <path d="M 10 9 L 10 12 M 8.5 10.5 L 11.5 10.5" stroke="#096dd9" stroke-width="0.7"/>
  </svg>`,

  'epx:Transaction': `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#e6f7ff" stroke="#096dd9" stroke-width="2"/>
    <rect x="3" y="3" width="14" height="10" rx="1" fill="none" stroke="#096dd9" stroke-width="1"/>
    <rect x="6" y="8" width="8" height="5" rx="1" fill="none" stroke="#096dd9" stroke-width="0.8"/>
    <path d="M 10 9 L 10 12 M 8.5 10.5 L 11.5 10.5" stroke="#096dd9" stroke-width="0.7"/>
  </svg>`,

  // Gateways
  'epx:ExclusiveGateway': `<svg width="20" height="20" viewBox="0 0 20 20">
    <polygon points="10,2 18,10 10,18 2,10" fill="#fffbe6" stroke="#faad14" stroke-width="1.5"/>
    <path d="M 7 7 L 13 13 M 13 7 L 7 13" stroke="#faad14" stroke-width="1.5" fill="none"/>
  </svg>`,

  'epx:ParallelGateway': `<svg width="20" height="20" viewBox="0 0 20 20">
    <polygon points="10,2 18,10 10,18 2,10" fill="#e6fffb" stroke="#13c2c2" stroke-width="1.5"/>
    <path d="M 10 5 L 10 15 M 5 10 L 15 10" stroke="#13c2c2" stroke-width="2" fill="none"/>
  </svg>`,

  'epx:InclusiveGateway': `<svg width="20" height="20" viewBox="0 0 20 20">
    <polygon points="10,2 18,10 10,18 2,10" fill="#fff0f6" stroke="#eb2f96" stroke-width="1.5"/>
    <circle cx="10" cy="10" r="4" fill="none" stroke="#eb2f96" stroke-width="1.5"/>
  </svg>`,

  'epx:ComplexGateway': `<svg width="20" height="20" viewBox="0 0 20 20">
    <polygon points="10,2 18,10 10,18 2,10" fill="#fafafa" stroke="#595959" stroke-width="1.5"/>
    <path d="M 10 5 L 10 15 M 5 10 L 15 10 M 7 7 L 13 13 M 13 7 L 7 13" stroke="#595959" stroke-width="1.2" fill="none"/>
  </svg>`,

  'epx:EventBasedGateway': `<svg width="20" height="20" viewBox="0 0 20 20">
    <polygon points="10,2 18,10 10,18 2,10" fill="#f6ffed" stroke="#389e0d" stroke-width="1.5"/>
    <circle cx="10" cy="10" r="5" fill="none" stroke="#389e0d" stroke-width="1"/>
    <circle cx="10" cy="10" r="3.5" fill="none" stroke="#389e0d" stroke-width="1"/>
  </svg>`,

  // Events
  'epx:StartEvent': `<svg width="20" height="20" viewBox="0 0 20 20">
    <circle cx="10" cy="10" r="8" fill="#f6ffed" stroke="#52c41a" stroke-width="1.5"/>
  </svg>`,

  'epx:EndEvent': `<svg width="20" height="20" viewBox="0 0 20 20">
    <circle cx="10" cy="10" r="8" fill="#fff1f0" stroke="#f5222d" stroke-width="3"/>
  </svg>`,

  'epx:IntermediateEvent': `<svg width="20" height="20" viewBox="0 0 20 20">
    <circle cx="10" cy="10" r="8" fill="#fff" stroke="#1890ff" stroke-width="1.5"/>
    <circle cx="10" cy="10" r="5" fill="none" stroke="#1890ff" stroke-width="1.2"/>
  </svg>`,

  'epx:IntermediateThrowEvent': `<svg width="20" height="20" viewBox="0 0 20 20">
    <circle cx="10" cy="10" r="8" fill="#fff" stroke="#1890ff" stroke-width="1.5"/>
    <circle cx="10" cy="10" r="5" fill="none" stroke="#1890ff" stroke-width="1.2"/>
    <polygon points="10,5 13.5,11.5 6.5,11.5" fill="#1890ff" stroke="#1890ff" stroke-width="0.5"/>
  </svg>`,

  'epx:BoundaryEvent': `<svg width="20" height="20" viewBox="0 0 20 20">
    <circle cx="10" cy="10" r="8" fill="#fff2e8" stroke="#fa541c" stroke-width="1.5"/>
    <circle cx="10" cy="10" r="5" fill="none" stroke="#fa541c" stroke-width="1"/>
  </svg>`,

  'eventSubProcess': `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#e6f7ff" stroke="#096dd9" stroke-width="2" stroke-dasharray="3,2"/>
    <rect x="6" y="8" width="8" height="5" rx="1" fill="none" stroke="#096dd9" stroke-width="0.8"/>
    <path d="M 10 9 L 10 12 M 8.5 10.5 L 11.5 10.5" stroke="#096dd9" stroke-width="0.7"/>
  </svg>`,
};

// ── Tab definitions (mirrors Palette left panel groups) ─────────────────────

const TABS = {
  tasks: {
    label: 'Tareas',
    groups: [
      {
        label: 'Tipos',
        items: [
          { type: 'epx:UserTask',         icon: 'epx:UserTask',         label: 'User Task',         shortLabel: 'User Task' },
          { type: 'epx:ServiceTask',       icon: 'epx:ServiceTask',       label: 'Service Task',       shortLabel: 'Service' },
          { type: 'epx:ScriptTask',        icon: 'epx:ScriptTask',        label: 'Script Task',        shortLabel: 'Script' },
          { type: 'epx:ManualTask',        icon: 'epx:ManualTask',        label: 'Manual Task',        shortLabel: 'Manual' },
          { type: 'epx:SendTask',          icon: 'epx:SendTask',          label: 'Send Task',          shortLabel: 'Send' },
          { type: 'epx:ReceiveTask',       icon: 'epx:ReceiveTask',       label: 'Receive Task',       shortLabel: 'Receive' },
          { type: 'epx:BusinessRuleTask',  icon: 'epx:BusinessRuleTask',  label: 'Business Rule Task', shortLabel: 'Rule' },
          { type: 'epx:CallActivity',      icon: 'epx:CallActivity',      label: 'Call Activity',      shortLabel: 'Call' },
        ],
      },
    ],
  },
  gateways: {
    label: 'Gateways',
    groups: [
      {
        label: 'Tipos',
        items: [
          { type: 'epx:ExclusiveGateway',  icon: 'epx:ExclusiveGateway',  label: 'Exclusive Gateway',   shortLabel: 'Exclusivo' },
          { type: 'epx:ParallelGateway',   icon: 'epx:ParallelGateway',   label: 'Parallel Gateway',    shortLabel: 'Paralelo' },
          { type: 'epx:InclusiveGateway',  icon: 'epx:InclusiveGateway',  label: 'Inclusive Gateway',   shortLabel: 'Inclusivo' },
          { type: 'epx:ComplexGateway',    icon: 'epx:ComplexGateway',    label: 'Complex Gateway',     shortLabel: 'Complejo' },
          { type: 'epx:EventBasedGateway', icon: 'epx:EventBasedGateway', label: 'Event-Based Gateway', shortLabel: 'Por Evento' },
        ],
      },
    ],
  },
  events: {
    label: 'Eventos',
    groups: [
      {
        label: 'Tipos',
        items: [
          { type: 'epx:StartEvent',             icon: 'epx:StartEvent',             label: 'Start Event',        shortLabel: 'Start' },
          { type: 'epx:EndEvent',               icon: 'epx:EndEvent',               label: 'End Event',          shortLabel: 'End' },
          { type: 'epx:IntermediateEvent',      icon: 'epx:IntermediateEvent',      label: 'Intermediate Catch', shortLabel: 'Catch' },
          { type: 'epx:IntermediateThrowEvent', icon: 'epx:IntermediateThrowEvent', label: 'Intermediate Throw', shortLabel: 'Throw' },
          { type: 'epx:BoundaryEvent',          icon: 'epx:BoundaryEvent',          label: 'Boundary Event',     shortLabel: 'Boundary' },
        ],
      },
    ],
  },
  subProcesses: {
    label: 'Subprocesos',
    groups: [
      {
        label: 'Tipos',
        items: [
          { type: 'epx:SubProcess',  icon: 'epx:SubProcess',  label: 'Sub-Process',       shortLabel: 'Sub Proceso' },
          { type: 'epx:Transaction', icon: 'epx:Transaction', label: 'Transaction',        shortLabel: 'Transacción' },
          { type: 'epx:SubProcess',  icon: 'eventSubProcess', label: 'Event Sub-Process',  shortLabel: 'Ev. SubProc', properties: { triggeredByEvent: true } },
        ],
      },
    ],
  },
};

// ── Which tab is active for a given element type ─────────────────────────────

function getActiveTab(nodeType, nodeProps) {
  if (nodeType.includes('Gateway')) return 'gateways';
  if (nodeType.includes('Event')) return 'events';
  if (nodeType === 'epx:SubProcess' || nodeType === 'epx:Transaction') return 'subProcesses';
  return 'tasks';
}

// ── CSS (injected once) ──────────────────────────────────────────────────────

let cssInjected = false;

function injectCSS() {
  if (cssInjected) return;
  cssInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    /* ── Popover container ── */
    .epx-replace-menu {
      position: absolute;
      width: 292px;
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.07);
      z-index: 250;
      overflow: hidden;
      font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
      font-size: 12px;
    }

    /* ── Header ── */
    .epx-rm-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 14px;
      border-bottom: 1px solid #f1f5f9;
    }
    .epx-rm-title {
      font-size: 10px;
      font-weight: 700;
      color: #0f172a;
      letter-spacing: 0.07em;
      text-transform: uppercase;
    }
    .epx-rm-close {
      width: 22px;
      height: 22px;
      border-radius: 5px;
      border: none;
      background: transparent;
      color: #94a3b8;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      line-height: 1;
      padding: 0;
    }
    .epx-rm-close:hover {
      background: #f1f5f9;
      color: #475569;
    }

    /* ── Tabs ── */
    .epx-rm-tabs {
      display: flex;
      padding: 0 14px;
      border-bottom: 1px solid #f1f5f9;
    }
    .epx-rm-tab {
      padding: 7px 10px;
      font-size: 11px;
      color: #64748b;
      cursor: pointer;
      border-bottom: 2px solid transparent;
      margin-bottom: -1px;
      user-select: none;
      transition: color 0.1s;
    }
    .epx-rm-tab:hover {
      color: #1d4ed8;
    }
    .epx-rm-tab.epx-rm-tab-active {
      color: #1d4ed8;
      border-bottom-color: #1d4ed8;
      font-weight: 600;
    }

    /* ── Info banner ── */
    .epx-rm-banner {
      display: flex;
      align-items: center;
      gap: 7px;
      padding: 6px 14px;
      background: #eff6ff;
      border-bottom: 1px solid #dbeafe;
      font-size: 10px;
      color: #1d4ed8;
      font-weight: 500;
    }
    .epx-rm-banner-icon {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #1d4ed8;
      color: #ffffff;
      font-size: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-weight: 700;
    }

    /* ── Scrollable body ── */
    .epx-rm-body {
      max-height: 280px;
      overflow-y: auto;
      padding-bottom: 6px;
    }
    .epx-rm-body::-webkit-scrollbar {
      width: 4px;
    }
    .epx-rm-body::-webkit-scrollbar-thumb {
      background: #e2e8f0;
      border-radius: 2px;
    }

    /* ── Group label ── */
    .epx-rm-group-label {
      font-size: 9px;
      font-weight: 700;
      color: #94a3b8;
      letter-spacing: 0.09em;
      text-transform: uppercase;
      padding: 9px 13px 4px;
    }

    /* ── Type grid ── */
    .epx-rm-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 3px;
      padding: 0 9px 4px;
    }

    /* ── Type item ── */
    .epx-rm-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 3px;
      padding: 6px 3px 5px;
      border-radius: 7px;
      cursor: pointer;
      border: 1.5px solid transparent;
      transition: background 0.1s, border-color 0.1s;
      user-select: none;
    }
    .epx-rm-item:hover {
      background: #f8fafc;
      border-color: #e2e8f0;
    }
    .epx-rm-item.epx-rm-item-active {
      background: #eff6ff;
      border-color: #3b82f6;
    }

    /* ── Icon box ── */
    .epx-rm-icon {
      width: 40px;
      height: 30px;
      border-radius: 5px;
      border: 1.5px solid #e2e8f0;
      background: #f8fafc;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: border-color 0.1s, background 0.1s;
    }
    .epx-rm-icon svg {
      width: 20px;
      height: 20px;
    }
    .epx-rm-item:hover .epx-rm-icon {
      border-color: #bfdbfe;
      background: #f0f9ff;
    }
    .epx-rm-item.epx-rm-item-active .epx-rm-icon {
      border-color: #3b82f6;
      background: #dbeafe;
    }

    /* ── Item label ── */
    .epx-rm-item-label {
      font-size: 9px;
      color: #64748b;
      text-align: center;
      line-height: 1.25;
    }
    .epx-rm-item.epx-rm-item-active .epx-rm-item-label {
      color: #1d4ed8;
      font-weight: 600;
    }
  `;
  document.head.appendChild(style);
}

// ── Helpers ──────────────────────────────────────────────────────────────────

// Properties that are safe to preserve when swapping types
const PRESERVED_PROPS = ['name', 'code', 'guid', 'url', 'documentation'];

function isActiveItem(item, nodeData) {
  const currentType = nodeData.type;
  const currentProps = nodeData.properties || {};
  if (item.type !== currentType) return false;
  if (item.properties) {
    for (const [k, v] of Object.entries(item.properties)) {
      if (currentProps[k] !== v) return false;
    }
    return true;
  }
  // Plain SubProcess (not event-triggered): confirm node doesn't have triggeredByEvent
  if (currentType === 'epx:SubProcess' && !item.properties) {
    return !currentProps.triggeredByEvent;
  }
  return true;
}

// ── ReplaceMenu class ────────────────────────────────────────────────────────

export class ReplaceMenu {
  constructor(lf, i18n) {
    this._lf = lf;
    this._i18n = i18n || {};
    this._menuEl = null;
    this._onDocClick = null;
    this._activeTab = 'tasks';

    injectCSS();
  }

  /**
   * Show the replace popover near the given node.
   * @param {Object} nodeData - The node data (id, type, properties, x, y)
   */
  show(nodeData, _anchorEl) {
    this.hide();

    this._activeTab = getActiveTab(nodeData.type, nodeData.properties);
    this._menuEl = this._buildPanel(nodeData);

    const container = this._lf.graphModel.rootEl;
    if (!container) return;
    container.appendChild(this._menuEl);

    this._positionMenu(nodeData);

    // Close on outside click (delayed to avoid immediate close)
    setTimeout(() => {
      this._onDocClick = (e) => {
        if (this._menuEl && !this._menuEl.contains(e.target)) {
          this.hide();
        }
      };
      document.addEventListener('click', this._onDocClick);
    }, 0);
  }

  hide() {
    if (this._menuEl && this._menuEl.parentNode) {
      this._menuEl.parentNode.removeChild(this._menuEl);
    }
    this._menuEl = null;

    if (this._onDocClick) {
      document.removeEventListener('click', this._onDocClick);
      this._onDocClick = null;
    }
  }

  // ── DOM builder ─────────────────────────────────────────────────────────────

  _buildPanel(nodeData) {
    const panel = document.createElement('div');
    panel.className = 'epx-replace-menu';

    // Header
    panel.appendChild(this._buildHeader());

    // Tabs row
    const tabsRow = document.createElement('div');
    tabsRow.className = 'epx-rm-tabs';
    for (const key of Object.keys(TABS)) {
      const tab = this._buildTab(key, TABS[key].label, nodeData);
      tabsRow.appendChild(tab);
    }
    panel.appendChild(tabsRow);

    // Banner
    panel.appendChild(this._buildBanner());

    // Body with groups
    const body = document.createElement('div');
    body.className = 'epx-rm-body';
    this._renderTabContent(body, nodeData, this._activeTab);
    panel.appendChild(body);

    this._body = body;
    this._nodeData = nodeData;

    return panel;
  }

  _buildHeader() {
    const hdr = document.createElement('div');
    hdr.className = 'epx-rm-header';

    const title = document.createElement('div');
    title.className = 'epx-rm-title';
    title.textContent = 'Tipo de Elemento';
    hdr.appendChild(title);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'epx-rm-close';
    closeBtn.innerHTML = '&#x2715;';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.hide();
    });
    hdr.appendChild(closeBtn);

    return hdr;
  }

  _buildBanner() {
    const banner = document.createElement('div');
    banner.className = 'epx-rm-banner';

    const icon = document.createElement('div');
    icon.className = 'epx-rm-banner-icon';
    icon.textContent = '✓';
    banner.appendChild(icon);

    const text = document.createElement('span');
    text.textContent = 'Cambia el tipo del nodo seleccionado';
    banner.appendChild(text);

    return banner;
  }

  _buildTab(key, label, nodeData) {
    const tab = document.createElement('div');
    tab.className = 'epx-rm-tab' + (key === this._activeTab ? ' epx-rm-tab-active' : '');
    tab.textContent = label;
    tab.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this._activeTab === key) return;
      this._activeTab = key;

      // Update tab styles
      const allTabs = this._menuEl.querySelectorAll('.epx-rm-tab');
      allTabs.forEach((t, i) => {
        t.classList.toggle('epx-rm-tab-active', Object.keys(TABS)[i] === key);
      });

      // Re-render body
      this._body.innerHTML = '';
      this._renderTabContent(this._body, nodeData, key);
    });
    return tab;
  }

  _renderTabContent(container, nodeData, tabKey) {
    const tabDef = TABS[tabKey];
    if (!tabDef) return;

    for (const group of tabDef.groups) {
      const groupLabel = document.createElement('div');
      groupLabel.className = 'epx-rm-group-label';
      groupLabel.textContent = group.label;
      container.appendChild(groupLabel);

      const grid = document.createElement('div');
      grid.className = 'epx-rm-grid';

      for (const item of group.items) {
        grid.appendChild(this._buildItem(item, nodeData));
      }

      container.appendChild(grid);
    }
  }

  _buildItem(item, nodeData) {
    const el = document.createElement('div');
    el.className = 'epx-rm-item' + (isActiveItem(item, nodeData) ? ' epx-rm-item-active' : '');

    // Icon box
    const iconBox = document.createElement('div');
    iconBox.className = 'epx-rm-icon';
    iconBox.innerHTML = TYPE_ICONS[item.icon] || TYPE_ICONS[item.type] || '';
    el.appendChild(iconBox);

    // Label
    const label = document.createElement('div');
    label.className = 'epx-rm-item-label';
    label.textContent = item.shortLabel;
    el.appendChild(label);

    // Click → replace
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      if (isActiveItem(item, nodeData)) {
        this.hide();
        return;
      }
      this._replaceElement(nodeData, item);
      this.hide();
    });

    return el;
  }

  // ── Positioning ─────────────────────────────────────────────────────────────

  _positionMenu(nodeData) {
    if (!this._menuEl) return;

    const model = this._lf.graphModel.getElement(nodeData.id);
    if (!model) return;

    const w = model.width || 60;
    const h = model.height || 60;

    // Position: to the right of the node, vertically centered
    const canvasX = nodeData.x + w / 2 + 14;
    const canvasY = nodeData.y - h / 2 - 4;

    const [htmlX, htmlY] = this._lf.graphModel.transformModel.CanvasPointToHtmlPoint([canvasX, canvasY]);

    const container = this._lf.graphModel.rootEl;
    const containerRect = container.getBoundingClientRect();

    const menuW = 292;
    const menuH = 380; // approximate

    // Clamp so it doesn't go off-screen
    let left = htmlX;
    let top = htmlY;

    if (left + menuW > containerRect.width - 8) {
      // Flip to left of node
      const [leftX] = this._lf.graphModel.transformModel.CanvasPointToHtmlPoint([nodeData.x - w / 2 - menuW - 14, canvasY]);
      left = leftX;
    }
    if (top + menuH > containerRect.height - 8) {
      top = Math.max(8, containerRect.height - menuH - 8);
    }
    if (top < 8) top = 8;

    this._menuEl.style.left = left + 'px';
    this._menuEl.style.top = top + 'px';
  }

  // ── Replace logic (unchanged) ─────────────────────────────────────────────

  /**
   * Replace an element with a new type, preserving connections and compatible properties.
   */
  _replaceElement(nodeData, replacement) {
    const lf = this._lf;
    const oldModel = lf.graphModel.getElement(nodeData.id);
    if (!oldModel) return;

    const oldType = nodeData.type;
    const newType = replacement.type;
    const isSameType = oldType === newType;

    // If same type and the replacement is already active (e.g. same SubProcess variant), nothing to do
    if (isSameType && isActiveItem(replacement, nodeData)) return;

    // Collect connections
    const { edges } = lf.getGraphData();
    const incomingEdges = edges.filter(e => e.targetNodeId === nodeData.id);
    const outgoingEdges = edges.filter(e => e.sourceNodeId === nodeData.id);

    // Collect preserved properties
    const oldProps = nodeData.properties || {};
    const newProps = {};
    for (const key of PRESERVED_PROPS) {
      if (oldProps[key] !== undefined) {
        newProps[key] = oldProps[key];
      }
    }

    // Apply replacement-specific properties (e.g. triggeredByEvent for EventSubProcess)
    if (replacement.properties) {
      Object.assign(newProps, replacement.properties);
    }

    // Preserve boundary attachment
    if (oldProps.attachedToRef) {
      newProps.attachedToRef = oldProps.attachedToRef;
    }

    // Delete old node (this also deletes connected edges)
    lf.deleteNode(nodeData.id);

    // Create new node at same position with same ID
    const newNode = lf.addNode({
      id: nodeData.id,
      type: newType,
      x: nodeData.x,
      y: nodeData.y,
      properties: newProps,
    });

    if (!newNode) return;

    // Restore connections
    for (const edge of incomingEdges) {
      lf.addEdge({
        id: edge.id,
        type: edge.type,
        sourceNodeId: edge.sourceNodeId,
        targetNodeId: newNode.id,
        properties: edge.properties,
      });
    }

    for (const edge of outgoingEdges) {
      lf.addEdge({
        id: edge.id,
        type: edge.type,
        sourceNodeId: newNode.id,
        targetNodeId: edge.targetNodeId,
        properties: edge.properties,
      });
    }
  }

  destroy() {
    this.hide();
  }
}
